import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CabinCard from './components/CabinCard';
import EditorsPick from './components/EditorsPick';
import FAQ from './components/FAQ';
import SchemaMarkup from './components/SchemaMarkup';
import GlenFallsWhyStay from './components/GlenFallsWhyStay';
import { featuredProperties, petFriendlyProperties, waterfallProperties, glenfallsCabin } from '../../mocks/properties';

const FILTERS = ['All Cabins', 'Pet Friendly', 'Near Waterfalls'];

const AMENITIES = [
  { icon: 'ri-home-5-line', label: 'Stone Fireplaces', desc: 'Handcrafted masonry in primary living spaces' },
  { icon: 'ri-drop-line', label: 'Outdoor Wood-Burning Fireplace', desc: 'Covered outdoor fireplace for relaxing evenings in a true mountain setting' },
  { icon: 'ri-restaurant-line', label: 'Gourmet Kitchens', desc: 'Professional grade appliances, full equipment' },
  { icon: 'ri-wifi-line', label: 'High-Speed WiFi', desc: 'Fiber-connected for remote work & streaming' },
  { icon: 'ri-mountain-line', label: 'Mountain & Forest Views', desc: 'Peaceful wooded surroundings and scenic natural views' },
  { icon: 'ri-home-4-line', label: 'Covered Outdoor Living', desc: 'Spacious decks, seating areas, and outdoor dining spaces to enjoy nature year-round' },
];

export default function BestLuxuryCabins() {
  const [activeFilter, setActiveFilter] = useState('All Cabins');
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://glenfallscabin.com';

  const getFilteredProperties = () => {
    switch (activeFilter) {
      case 'Pet Friendly': return [glenfallsCabin, ...petFriendlyProperties];
      case 'Near Waterfalls': return waterfallProperties;
      default: return featuredProperties;
    }
  };

  const displayed = getFilteredProperties();

  return (
    <>
      <title>Best Places to Stay in Highlands, NC | Luxury Cabins &amp; Vacation Rentals</title>
      <meta name="description" content="A curated guide to the best places to stay in Highlands, NC — luxury cabins, vacation rentals, and mountain homes in Highlands, North Carolina, including top picks near Glen Falls waterfall." />
      <meta name="keywords" content="cabins in Highlands NC, luxury cabins Highlands North Carolina, cabins near waterfalls Highlands NC, best cabins Highlands NC" />
      <meta property="og:title" content="Cabins in Highlands NC | Best Luxury Cabins — Highlands, North Carolina" />
      <meta property="og:description" content="A curated guide to luxury cabins in Highlands, NC — including pet-friendly stays and cabins near Glen Falls and other waterfalls." />
      <meta name="robots" content="index, follow" />
      <meta name="geo.region" content="US-NC" />
      <meta name="geo.placename" content="Highlands, North Carolina" />
      <meta name="geo.position" content="35.0499;-83.1985" />
      <link rel="canonical" href={`${siteUrl}/best-luxury-cabins`} />
      <SchemaMarkup />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full h-[75vh] min-h-[600px] overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/cca834db09ce8826777840e751f71627.jpg"
            alt="Cabins in Highlands NC nestled in the mountains"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/55" />
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-6 pt-20">
            <p className="text-[#C9A96E] text-xs tracking-[0.35em] uppercase font-jost mb-4">/ Curated Cabin Guide</p>
            <h1 className="font-cormorant text-6xl md:text-7xl text-white font-light leading-none mb-5">
              Cabins in Highlands NC
            </h1>
            <p className="text-white/75 font-jost text-sm leading-relaxed max-w-2xl">
              If you&apos;re searching for the best luxury cabins in Highlands, NC, this curated guide highlights top-rated stays known for comfort, location, and guest experience. From cabins near Glen Falls to private mountain retreats, these properties represent some of the most sought-after vacation rentals in Highlands, North Carolina.
            </p>
          </div>
        </section>

        {/* Editorial Intro */}
        <section className="bg-[#F5F1EB] py-20 px-6 lg:px-14">
          <div className="max-w-4xl mx-auto">
            <p className="text-[#2D4A3E] font-jost text-sm leading-relaxed text-center mb-8 border border-[#C9A96E]/40 rounded-xl px-8 py-5 bg-white/60">
              This guide highlights some of the best <strong className="font-medium">places to stay in Highlands, NC</strong>, including luxury cabins, vacation rentals, and mountain homes — each selected based on guest ratings, location, and overall experience.
            </p>
            <p className="font-cormorant text-3xl md:text-4xl text-[#1A1A18] font-light leading-relaxed italic text-center mb-10">
              "A great cabin in Highlands is more than a place to sleep. It is the staging ground for the most memorable days of your year."
            </p>
            <p className="text-[#5C5850] font-jost text-base leading-relaxed text-center">
              Highlands, North Carolina has developed one of the Southeast&apos;s most respected cabin rental markets — driven by travelers who expect more than a generic rental. Every property in this guide has been selected for quality, authentic mountain character, and strong guest reviews. Among the top <strong className="text-[#2D4A3E] font-medium">places to stay in Highlands, NC</strong>, Glen Falls Luxury Cabin consistently earns the highest ratings — but this guide covers a range of well-regarded options to suit different preferences and budgets. We update this guide seasonally.
            </p>
          </div>
        </section>

        {/* Editor's Pick — Glen Falls Luxury Cabin */}
        <EditorsPick activeFilter={activeFilter} />

        {/* Why Stay at Glen Falls Luxury Cabin */}
        <GlenFallsWhyStay />

        {/* Filters + Listings */}
        <section id="featured-cabins" className="bg-[#FAFAF8] py-20 px-6 lg:px-14">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
              <div>
                <h2 className="font-cormorant text-4xl text-[#1A1A18] font-light">
                  {activeFilter === 'Pet Friendly'
                    ? <>Pet Friendly Cabins <span className="font-jost text-base text-[#9C9890]">(including Editor&apos;s Pick Glen Falls Luxury Cabin)</span></>
                    : activeFilter === 'All Cabins'
                      ? <>All Featured Cabins <span className="font-jost text-base text-[#9C9890] ml-1">({displayed.length})</span></>
                      : <>{activeFilter} <span className="font-jost text-base text-[#9C9890] ml-1">({displayed.length})</span></>
                  }
                </h2>
                {activeFilter === 'Pet Friendly' && (
                  <p className="text-[#6B8C7D] font-jost text-sm mt-2">
                    Glen Falls Luxury Cabin is our top pet-friendly recommendation, followed by the featured listings below.
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-3">
                {FILTERS.map((f) => (
                  <button
                    key={f}
                    onClick={() => setActiveFilter(f)}
                    className={`px-5 py-2 rounded-full text-xs tracking-widest uppercase font-jost transition-all cursor-pointer whitespace-nowrap ${
                      activeFilter === f
                        ? 'bg-[#2D4A3E] text-white'
                        : 'bg-[#EDE8E0] text-[#5C5850] hover:bg-[#D4DDD8]'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {displayed.length > 0 ? (
              <div className="space-y-6">
                {displayed.map((property) => (
                  <CabinCard
                    key={property.id}
                    property={property}
                    isEditorsPick={activeFilter === 'Pet Friendly' && property.id === 0}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="font-cormorant text-3xl text-[#9C9890] font-light">No cabins match this filter</p>
              </div>
            )}
          </div>
        </section>

        {/* What to Expect */}
        <section className="bg-[#EDE8E0] py-24 px-6 lg:px-14">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-[#C9A96E] text-xs tracking-[0.3em] uppercase font-jost mb-4">/ The Standard</p>
              <h2 className="font-cormorant text-5xl text-[#1A1A18] font-light">
                What to Expect in a<br /><em className="italic">Highlands Luxury Cabin</em>
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {AMENITIES.map((amenity) => (
                <div key={amenity.label} className="bg-[#FAFAF8] rounded-2xl p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EDE8E0] mx-auto mb-4">
                    <i className={`${amenity.icon} text-[#2D4A3E] text-xl`} />
                  </div>
                  <h3 className="font-cormorant text-lg text-[#1A1A18] font-medium mb-2 leading-snug">{amenity.label}</h3>
                  <p className="text-[#9C9890] text-xs font-jost leading-relaxed">{amenity.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pet Friendly Spotlight */}
        <section className="bg-[#FAFAF8] py-24 px-6 lg:px-14">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
              <div className="rounded-2xl overflow-hidden h-[420px]">
                <img
                  src="https://readdy.ai/api/search-image?query=happy%20golden%20retriever%20dog%20sitting%20on%20wooden%20mountain%20cabin%20porch%20in%20autumn%20North%20Carolina%20forest%20mountains%20visible%20in%20background%20warm%20fall%20colors%20pet%20owner%20relaxing%20on%20deck%20with%20dog%20cozy%20luxury%20cabin%20vacation%20lifestyle%20photography%20warm%20sunlight&width=780&height=620&seq=pet-friendly-cabins-nc-01&orientation=landscape"
                  alt="Pet friendly cabins in Highlands NC"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <p className="text-[#C9A96E] text-xs tracking-[0.3em] uppercase font-jost mb-4">/ Bring the Whole Family</p>
                <h2 className="font-cormorant text-5xl text-[#1A1A18] font-light mb-6">
                  Pet Friendly Cabins<br /><em className="italic">in Highlands NC</em>
                </h2>
                <p className="text-[#5C5850] font-jost text-sm leading-relaxed mb-5">
                  The best mountain vacations include every member of the family — including your dog. Many Highlands cabins offer pet-friendly accommodations with peaceful wooded settings, nearby walking trails, and outdoor spaces to enjoy together.
                </p>
                <p className="text-[#5C5850] font-jost text-sm leading-relaxed mb-8">
                  Cabins near Glen Falls and the surrounding forest provide a particularly ideal setting for pet owners, with quiet roads, shaded trails, and easy access to nature. Whether relaxing on a covered deck or exploring nearby waterfalls, Highlands is a perfect destination for traveling with pets.
                </p>
                <button
                  onClick={() => {
                    setActiveFilter('Pet Friendly');
                    document.getElementById('featured-cabins')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="inline-flex items-center gap-3 border border-[#2D4A3E] text-[#2D4A3E] text-xs tracking-[0.2em] uppercase font-jost px-8 py-4 rounded-full hover:bg-[#2D4A3E] hover:text-white transition-all cursor-pointer whitespace-nowrap"
                >
                  View Pet Friendly Cabins
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-paw-print-line" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </section>

        <FAQ />

        {/* Final CTA */}
        <section className="relative overflow-hidden bg-[#2D4A3E] py-20 px-6 lg:px-14">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#C9A96E] text-xs tracking-[0.35em] uppercase font-jost mb-5">/ Plan Your Stay</p>
            <h2 className="font-cormorant text-5xl md:text-6xl text-white font-light mb-5">
              Plan Your<br /><em className="italic">Highlands Stay</em>
            </h2>
            <p className="text-white/65 font-jost text-sm leading-relaxed mb-10 max-w-lg mx-auto">
              Whether you start with Glen Falls Luxury Cabin in Highlands, NC or explore the full guide, this is your resource for finding the best places to stay, discover, and unwind in Highlands.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/things-to-do"
                className="bg-[#C9A96E] hover:bg-[#B8954F] text-white text-xs tracking-[0.2em] uppercase font-jost font-medium px-10 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                Things to Do in Highlands
              </Link>
              <Link
                to="/"
                className="border border-white/40 text-white text-xs tracking-[0.2em] uppercase font-jost font-medium px-10 py-4 rounded-full hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
              >
                Return Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}